import { useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import { useActiveCompetition } from 'src/contexts/active-competition-context';

import { Iconify } from 'src/components/iconify';

import SelectCompetitionDialog from './select-competition-dialog';

// ----------------------------------------------------------------------

export function ActiveCompetitionDisplay({ sx, ...other }) {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const { 
    selectedCompetition, 
    activeCompetitions,
    loading, 
    hasSelectedCompetition,
    updateSelectedCompetition,
    // Legacy support - keep backward compatibility
    activeCompetition,
    hasActiveCompetition
  } = useActiveCompetition();



  // Use selected competition if available, otherwise fall back to legacy activeCompetition
  const displayCompetition = selectedCompetition || activeCompetition;
  const hasDisplayCompetition = hasSelectedCompetition || hasActiveCompetition;

  if (loading || !hasDisplayCompetition || !displayCompetition) {
    return null;
  }

  const handleDialogOpen = () => {
    if (activeCompetitions.length > 0) {
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCompetitionUpdate = async (competitionId) => {
    try {
      await updateSelectedCompetition(competitionId);
    } catch (error) {
      console.error('Failed to update selected competition:', error);
      throw error;
    }
  };

  const canChangeCompetition = activeCompetitions.length >= 1; // Allow interaction even with 1 competition

  return (
    <>
      <Box
        onClick={handleDialogOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleDialogOpen();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          cursor: canChangeCompetition ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          ...(canChangeCompetition && {
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              transform: 'translateY(-1px)',
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
            },
          }),
          ...sx,
        }}
        {...other}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mr: 0.5,
            fontWeight: 500,
          }}
        >
          Competition:
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            flex: 1,
          }}
        >
          {displayCompetition.Name}
        </Typography>
        <Iconify 
          icon="eva:settings-2-fill" 
          width={16} 
          sx={{ 
            color: 'primary.main',
            ml: 1,
            opacity: canChangeCompetition ? 0.7 : 0.3,
          }} 
        />
      </Box>

      <SelectCompetitionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleCompetitionUpdate}
        activeCompetitions={activeCompetitions}
        currentSelectedCompetitionId={displayCompetition?.Id}
      />
    </>
  );
}
